import React, { useContext } from 'react';
import pt from 'prop-types';
import { combine } from '/src/helpers/styles';
import Text from '../../../../../ui/text';
import Link from '../../../../../ui/link';
import Image from '../../../../../ui/image';
import Icon from '../../../../../ui/icon';
import * as styles from './styles.module.scss';
import { getCountryName } from '../../../../../../utils/constants';
import { MicrocopyContext } from '../../../../../../context/microcopy.context';
import { getCopy } from '../../../../../../helpers/copy';
import { useLocale } from '../../../../../../context/locale.context';
import { getProfileTypeId } from '../../../../../../helpers/getProfileTypeId';

const RankingTableRow = ({ data, index, type, withType, isGlobal }) => {
  const microcopy = useContext(MicrocopyContext);
  const { locale } = useLocale();

  // get appropriate number to show as label (e.g. Top 3)
  const getTopNumber = () => {
    const position = index + 1;
    const steps = [3, 10, 25, 50, 100];
    return steps.find((step) => position <= step);
  };

  const getRegionLabel = (region) =>
    getCopy(
      `filters.region.${region.replace(' ', '').toLowerCase()}`,
      microcopy
    );

  const withWrapper = (children) => {
    if (data.slug) {
      return (
        <Link
          link={{ linkToPage: data.slug }}
          useGatsbyLink={false}
          className={combine(
            styles.wrapper,
            (index === 0 || index === 3) && styles.borderBold
          )}
          key={index}
        >
          {children}
        </Link>
      );
    } else {
      return (
        <div
          className={combine(
            styles.wrapper,
            (index === 0 || index === 3) && styles.borderBold
          )}
          key={index}
        >
          {children}
        </div>
      );
    }
  };

  return withWrapper(
    <>
      <div className={styles.labelWrapper}>
        <div className={styles.rankingWrapper}>
          {index === 0 && !data.tied && (
            <Icon name="trophy" size="3x" className={styles.trophy} />
          )}
          <Text
            as="span"
            type="infotext"
            className={combine(
              styles.ranking,
              index === 0 && !data.tied && styles.first,
              index > 2 && styles.later
            )}
          >
            {index + 1}
          </Text>
        </div>
        {type === 'company' &&
          (index === 0 && !data.tied ? (
            <Text as="span" type="metanavi" variant="semibold">
              {withType
                ? `${getCopy('wdiRanking.labeledBest', microcopy)} ${
                    (data.continent && getRegionLabel(data.continent)) ||
                    (data.country && getCountryName(locale, data.country)) ||
                    (isGlobal ? getCopy('wdiRanking.global', microcopy) : '')
                  }`
                : getCopy('wdiRanking.nonLabeledBest', microcopy)}
            </Text>
          ) : (
            <Text as="span" type="metanavi">
              <b>{`${getCopy(
                'wdiRanking.top',
                microcopy
              )} ${getTopNumber()} `}</b>
              {withType
                ? `${
                    (data.continent && getRegionLabel(data.continent)) ||
                    (data.country && getCountryName(locale, data.country)) ||
                    (isGlobal ? getCopy('wdiRanking.global', microcopy) : '')
                  }`
                : ''}
            </Text>
          ))}
      </div>
      <div className={styles.descriptionWrapper}>
        {type === 'company' ? (
          <>
            {data.logoFile ? (
              <Image
                image={{ url: data.logoFile }}
                className={styles.logo}
                imgClassName={styles.image}
              />
            ) : (
              <div className={combine(styles.logo, styles.transparent)} />
            )}
            <div className={styles.companyWrapper}>
              <Text
                as="span"
                type="copy2"
                variant="semibold"
                className={combine(
                  styles.ranking,
                  styles.small,
                  index > 2 && styles.later
                )}
              >
                {index === 0 ? (
                  <Text as="span" type="copy2" variant="semibold">
                    {`${index + 1} | ${
                      withType
                        ? `${getCopy('wdiRanking.labeledBest', microcopy)} ${
                            (data.continent &&
                              getRegionLabel(data.continent)) ||
                            (data.country &&
                              getCountryName(locale, data.country)) ||
                            (isGlobal
                              ? getCopy('wdiRanking.global', microcopy)
                              : '')
                          }`
                        : getCopy('wdiRanking.nonLabeledBest', microcopy)
                    }`}
                  </Text>
                ) : (
                  <Text as="span" type="copy2" variant="semibold">
                    {`${index + 1} | ${getCopy(
                      'wdiRanking.top',
                      microcopy
                    )} ${getTopNumber()} `}
                    {withType
                      ? `${
                          (data.continent && getRegionLabel(data.continent)) ||
                          (data.country &&
                            getCountryName(locale, data.country)) ||
                          (isGlobal
                            ? getCopy('wdiRanking.global', microcopy)
                            : '')
                        }`
                      : ''}
                  </Text>
                )}
              </Text>
              <Text
                as="span"
                type="infotext"
                variant="semibold"
                className={styles.type}
              >
                {getCopy(
                  `filters.profiletypes.${getProfileTypeId(
                    data.profilTypeText
                  )}.wdi`,
                  microcopy
                )}
              </Text>
              <Text as="span" type="h3" variant="">
                {data.name}
              </Text>
            </div>
          </>
        ) : (
          <Text as="span" type="h3" variant="">
            {data.sectorName}
          </Text>
        )}
      </div>
      {data.slug && (
        <div className={styles.link}>
          <Icon name="arrow-right" size="2x" className={styles.arrow} />
        </div>
      )}
    </>
  );
};

RankingTableRow.propTypes = {
  data: pt.object.isRequired,
  index: pt.number.isRequired,
  type: pt.oneOf(['sectors', 'company']),
  isGlobal: pt.bool,
  withType: pt.bool,
};

RankingTableRow.defaultProps = {
  type: 'company',
  isGlobal: false,
  withType: true,
};

export default RankingTableRow;
